/** A tiny module-level store for app notices, so any hook or handler can raise a
 *  centered in-app message (a nicer replacement for window.alert / window.confirm)
 *  without threading a callback through the tree. One <Notice /> host subscribes
 *  and renders the current message. */
export interface AppNotice {
  id: number;
  message: string;
  /** "error" tints the accent red; default is neutral. */
  tone?: "error" | "info";
  /** A confirm prompt renders Cancel + Confirm and resolves the promise below. */
  confirm?: boolean;
  resolve?: (ok: boolean) => void;
}

type Listener = (n: AppNotice | null) => void;

let current: AppNotice | null = null;
let nextId = 1;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((l) => l(current));

/** Raise a centered notice. Returns immediately (non-blocking) — call and
 *  `return` in place of `window.alert(msg); return;`. */
export function notify(message: string, tone: "error" | "info" = "info"): void {
  current = { id: nextId++, message, tone };
  emit();
}

/** A centered yes/no prompt; resolves true on Confirm, false on Cancel/dismiss.
 *  Drop-in for `if (!window.confirm(msg)) return;` → `if (!(await confirmNotice(msg))) return;`. */
export function confirmNotice(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    current = { id: nextId++, message, confirm: true, resolve };
    emit();
  });
}

export function dismissNotice(ok = false): void {
  if (!current) return;
  const resolve = current.resolve;
  current = null;
  emit();
  resolve?.(ok);
}

export function subscribeNotice(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}
